import { imgUrl, thumbnail } from '../../utils/images';
import Datepicker, { DateType } from 'react-tailwindcss-datepicker';
import React, { useRef, useState } from 'react';
import { FileUploadResult, useUploadFiles } from '../../utils/upload';
import path from 'path-browserify';
import { InfoAlert } from '../../components/alerts/info';
import { SuccessAlert } from '../../components/alerts/success';
import { ErrorAlert } from '../../components/alerts/error';
import { parseISO } from 'date-fns';
import {
  TripResponse,
  useDeleteTripsByTripIdMutation,
  usePatchTripsByTripIdMutation,
} from '../../store/backendApi';
import { useNavigate } from 'react-router-dom';
import {
  CalendarIcon,
  CameraIcon,
  DocumentTextIcon,
  PhotoIcon,
  SparklesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../components/Button';

export interface HeaderOptionsPageProps {
  tripData: TripResponse;
}

const dateStr = (d: Date | undefined | null) => {
  return d?.toLocaleDateString('sv-SE') ?? undefined;
};

export const HeaderOptionsPage = (props: HeaderOptionsPageProps) => {
  const { tripData: trip } = props;
  const { uploadFiles } = useUploadFiles();
  const [changeHeaderSuccessful, setChangeHeaderSuccessful] = useState(false);
  const [changeHeaderFailed, setChangeHeaderFailed] = useState(false);
  const [changeHeaderLoading, setChangeHeaderLoading] = useState(false);

  const [tripName, setTripName] = useState<string>(props.tripData.title);
  const [tripDescription, setTripDescription] = useState<string>(props.tripData.description);
  const [tripHeader, setTripHeader] = useState<string>(props.tripData.headerFileName);
  const [tripDates, setTripDates] = useState<{ startDate: DateType; endDate: DateType } | null>(
    props.tripData.startDate && props.tripData.endDate
      ? { startDate: parseISO(props.tripData.startDate), endDate: parseISO(props.tripData.endDate) }
      : null,
  );
  const [patchTrip, { isLoading: patchTripLoading }] = usePatchTripsByTripIdMutation();
  const [deleteTrip, { isLoading: deleteTripLoading }] = useDeleteTripsByTripIdMutation();
  const navigate = useNavigate();
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasChanges =
    tripName !== trip.title ||
    tripDescription !== trip.description ||
    tripHeader !== trip.headerFileName ||
    dateStr(tripDates?.endDate) !== trip.endDate ||
    dateStr(tripDates?.startDate) !== trip.startDate;

  const updateTrip = async () => {
    await patchTrip({
      tripId: props.tripData.id,
      partialTripBodyRequest: {
        title: tripName ? tripName : undefined,
        description: tripDescription ? tripDescription : undefined,
        startDate: tripDates ? dateStr(tripDates.startDate) : undefined,
        endDate: tripDates ? dateStr(tripDates.endDate) : undefined,
        headerFileName: tripHeader ? tripHeader : undefined,
      },
    });
  };

  const reset = () => {
    setTripName(props.tripData.title);
    setTripDescription(props.tripData.description);
    setTripDates(
      props.tripData.startDate && props.tripData.endDate
        ? {
          startDate: parseISO(props.tripData.startDate),
          endDate: parseISO(props.tripData.endDate),
        }
        : null,
    );
  };

  const handleDelete = async () => {
    if (!deleteConfirmation) {
      setDeleteConfirmation(true);
      return;
    }

    await deleteTrip(props.tripData.id).unwrap();
    navigate('/');
  };

  const onFileUploaded = async (fileUploadResult: FileUploadResult) => {
    if (!fileUploadResult.success || !fileUploadResult.fileUpload) {
      console.error(fileUploadResult.error);
      showFailure();
      return;
    }
    const headerUrl = fileUploadResult.fileUpload.uploadUrl.pathname;
    setTripHeader(path.basename(headerUrl));
    showSuccess();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setChangeHeaderLoading(true);
    await uploadFiles({
      tripId: props.tripData.id,
      files: [file],
      onUploaded: onFileUploaded,
    });
  };

  const showSuccess = () => {
    setChangeHeaderLoading(false);
    setChangeHeaderSuccessful(true);
    setTimeout(() => {
      setChangeHeaderSuccessful(false);
    }, 2000);
  };

  const showFailure = () => {
    setChangeHeaderLoading(false);
    setChangeHeaderFailed(true);
    setTimeout(() => {
      setChangeHeaderFailed(false);
    }, 2000);
  };

  return (
    <div className="space-y-5">
      {/* Alerts */}
      {changeHeaderLoading && <InfoAlert text="Uploading header image..." />}
      {changeHeaderSuccessful && <SuccessAlert text="Header updated!" />}
      {changeHeaderFailed && <ErrorAlert text="Header upload failed" />}

      {/* Header Image Section */}
      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm font-medium text-main">
          <PhotoIcon className="w-4 h-4 text-brand" />
          Cover Image
        </label>
        <div className="relative rounded-xl overflow-hidden group aspect-[3/1] border-2 border-default shadow-sm">
          <img
            ref={imgRef}
            id="header-image"
            alt="Trip cover"
            src={
              tripHeader
                ? imgUrl(props.tripData.id, thumbnail(tripHeader))
                : '/default-trip-header.jpg'
            }
            className="object-cover w-full h-full"
          />

          {/* Camera Button Overlay */}
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={changeHeaderLoading}
            className="absolute bottom-3 right-3 p-2 rounded-full group-hover:scale-110 duration-200 shadow-lg"
            title="Change cover image"
          >
            {changeHeaderLoading ? (
              <div className="h-5 w-5 border-2 border-surface border-t-transparent rounded-full animate-spin" />
            ) : (
              <CameraIcon className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Trip Dates */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-main">
          <CalendarIcon className="w-4 h-4 text-brand" />
          When is your trip?
        </label>
        <Datepicker
          inputId={'trip-dates'}
          value={tripDates}
          onChange={(newValue) => setTripDates(newValue)}
        />
      </div>

      {/* Trip Title */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-main">
          <SparklesIcon className="w-4 h-4 text-brand" />
          Trip Title
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SparklesIcon className="h-5 w-5 text-disabled" />
          </div>
          <input
            id={'trip-name'}
            name={'trip name'}
            type={'text'}
            placeholder="e.g., Summer in Italy"
            value={tripName}
            onChange={(event) => setTripName(event.target.value)}
            className="block w-full pl-10 pr-3 py-3 rounded-lg bg-surface border border-interactive
              text-main placeholder:text-disabled font-medium
              focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent
              transition-all shadow-sm hover:border-interactive-hover"
          />
        </div>
      </div>

      {/* Trip Description */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-main">
          <DocumentTextIcon className="w-4 h-4 text-brand" />
          Description
        </label>
        <textarea
          id={'trip-desc'}
          name={'trip description'}
          rows={3}
          placeholder="Tell people about your amazing trip..."
          value={tripDescription}
          onChange={(event) => setTripDescription(event.target.value)}
          className="block w-full px-4 py-3 rounded-lg bg-surface border border-interactive
            text-main placeholder:text-disabled
            focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent
            transition-all shadow-sm hover:border-interactive-hover resize-none"
        />
      </div>

      {/* Action Buttons */}
      <div className="pt-3 space-y-3 border-t border-muted">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="primary"
            className="flex-1"
            disabled={patchTripLoading || !hasChanges}
            loading={patchTripLoading}
            onClick={() => updateTrip()}
          >
            {patchTripLoading ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button type="button" variant="secondary" onClick={reset} disabled={!hasChanges}>
            Reset
          </Button>
        </div>

        {/* Delete Trip Button */}
        {deleteConfirmation ? (
          <div className="space-y-2">
            <p className="text-sm text-error font-medium text-center flex items-center justify-center gap-2">
              <TrashIcon className="w-4 h-4" />
              Are you sure? This cannot be undone!
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="destructive"
                className="flex-1"
                disabled={deleteTripLoading}
                loading={deleteTripLoading}
                onClick={handleDelete}
              >
                {deleteTripLoading ? 'Deleting...' : 'Yes, Delete Trip'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setDeleteConfirmation(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            variant="destructive"
            fullWidth
            disabled={deleteTripLoading}
            onClick={handleDelete}
            iconBefore={<TrashIcon className="w-4 h-4" />}
          >
            Delete Trip
          </Button>
        )}
      </div>

      <input
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </div>
  );
};
